/**
 * The examiner's desk, `hierarchy` (`HierarchyDesk.logic.ts`): what was
 * read, by kind and most important first, each record once as a row that
 * opens in place; lookups that failed apart. The stage draws it beside a
 * live call and the saved interview beside its transcript.
 *
 * @module components/apps/probe/desk/HierarchyDesk
 */
import { memo, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";

import { cn } from "~/lib/utils";
import { DESK_LABEL_CLASS, LINKED_CLASS, type ProbeStageDesk } from "../ProbeDesk";
import { fitRows, hierarchyDeskOf, type HierarchyDesk as HierarchyDeskShape } from "./HierarchyDesk.logic";
import {
  DeskChecks,
  DeskFrame,
  ExpandingRecordRow,
  KindHeading,
  OpenOneRow,
  Quiet,
  ReadingNow,
  Rows,
  useOpenRows,
} from "./DeskParts";
import { byImportance, type DeskKind, type DeskRecord } from "./deskRecords";

/** Rows the desk drops per pass while it overflows: about one row's height, so it settles in a few. */
const ROW_PX = 64;

const HierarchyDesk = memo(function HierarchyDesk({
  desk,
  orgSegment,
  headingId,
}: {
  readonly desk: HierarchyDeskShape;
  readonly orgSegment: string;
  readonly headingId?: string | undefined;
}) {
  return (
    <DeskFrame headingId={headingId} variant="hierarchy">
      <ReadingNow reading={desk.reading} />
      <div className={cn(desk.reading.length > 0 && "mt-4")}>
        <HierarchyRead desk={desk} orgSegment={orgSegment} />
      </div>
    </DeskFrame>
  );
});

function HierarchyRead({
  desk,
  orgSegment,
}: {
  readonly desk: HierarchyDeskShape;
  readonly orgSegment: string;
}) {
  return (
    <OpenOneRow>
      <FittedKinds desk={desk} orgSegment={orgSegment} />
    </OpenOneRow>
  );
}

/** The desk's scrolling column's height, as it changes; null outside one. */
function useColumnHeight(root: RefObject<HTMLElement | null>): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const column = root.current?.closest?.("[data-probe-desk]");
    if (column === null || column === undefined || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setHeight(column.clientHeight));
    observer.observe(column);
    setHeight(column.clientHeight);
    return () => observer.disconnect();
  }, [root]);
  return height;
}

/**
 * Every kind read, filled to the column's height with its most important
 * rows (`fitRows`) and the rest of each kind folded into "N more", so the
 * column scrolls only once someone opens a row or a kind. Refitted when the
 * column resizes or what was read changes.
 */
function FittedKinds({
  desk,
  orgSegment,
}: {
  readonly desk: HierarchyDeskShape;
  readonly orgSegment: string;
}) {
  const rows = useOpenRows();
  const root = useRef<HTMLDivElement>(null);
  const height = useColumnHeight(root);
  const failed = desk.checks.filter((check) => check.kind === "failed");
  const groups = useMemo(
    () =>
      desk.groups.map((group) => ({
        kind: group.kind,
        records: [...group.opened, ...group.listed].toSorted(byImportance),
      })),
    [desk.groups],
  );
  const total = groups.reduce((sum, group) => sum + group.records.length, 0);
  const fitFor = `${height}:${total}:${desk.reading.length}:${failed.length}`;
  const [fit, setFit] = useState({ for: fitFor, budget: total });
  const budget = fit.for === fitFor ? fit.budget : total;
  const [expanded, setExpanded] = useState<ReadonlySet<DeskKind>>(new Set());
  const settled = rows?.open != null || expanded.size > 0;

  useLayoutEffect(() => {
    const column = root.current?.closest?.("[data-probe-desk]");
    if (settled || column === null || column === undefined || height === null) return;
    const excess = column.scrollHeight - column.clientHeight;
    const least = groups.length;
    if (excess > 1 && budget > least)
      setFit({ for: fitFor, budget: Math.max(least, budget - Math.max(1, Math.floor(excess / ROW_PX))) });
  });

  const shown = fitRows(
    groups.map((group) => group.records.length),
    budget,
  );
  return (
    <div ref={root} className="flex flex-col gap-4" data-probe-desk-read="">
      {groups.length === 0 && failed.length === 0 ? <Quiet>Nothing read yet.</Quiet> : null}
      {groups.map((group, index) => (
        <KindGroup
          key={group.kind}
          kind={group.kind}
          records={group.records}
          shown={shown[index]!}
          open={rows?.open ?? null}
          all={expanded.has(group.kind)}
          onAll={(all) =>
            setExpanded((was) => {
              const next = new Set(was);
              if (all) next.add(group.kind);
              else next.delete(group.kind);
              return next;
            })
          }
          orgSegment={orgSegment}
        />
      ))}
      {failed.length > 0 ? <DeskChecks checks={failed} /> : null}
    </div>
  );
}

function KindGroup({
  kind,
  records,
  shown,
  open,
  all,
  onAll,
  orgSegment,
}: {
  readonly kind: DeskKind;
  readonly records: ReadonlyArray<DeskRecord>;
  readonly shown: number;
  /** The open row, shown even when its kind folds it away. */
  readonly open: string | null;
  readonly all: boolean;
  readonly onAll: (all: boolean) => void;
  readonly orgSegment: string;
}) {
  const visible = all
    ? records
    : records.filter((record, index) => index < shown || record.key === open);
  const hidden = records.length - visible.length;
  return (
    <div className="flex flex-col gap-1.5" data-probe-desk-kind={kind}>
      <KindHeading kind={kind} count={records.length} />
      <Rows>
        {visible.map((record) => (
          <li key={record.key}>
            <ExpandingRecordRow record={record} orgSegment={orgSegment} />
          </li>
        ))}
        {hidden > 0 || all ? (
          <li>
            <button
              type="button"
              onClick={() => onAll(!all)}
              aria-expanded={all}
              className={cn("w-full cursor-pointer px-3 py-1.5 text-start text-xs", DESK_LABEL_CLASS, LINKED_CLASS)}
              data-probe-desk-more-rows=""
            >
              {all ? "Show fewer" : `${hidden} more`}
            </button>
          </li>
        ) : null}
      </Rows>
    </div>
  );
}

export const hierarchyStageDesk: ProbeStageDesk = (input, briefing) => {
  const desk = hierarchyDeskOf(input, briefing);
  return {
    read: desk.readCount,
    render: ({ orgSegment, headingId }) => (
      <HierarchyDesk desk={desk} orgSegment={orgSegment} headingId={headingId} />
    ),
  };
};
